import {
    countdownLabel,
    groupStationWork,
    nowLineIndex,
    peopleLabel,
    scheduleState,
    scheduleTimeRange,
    taskDueLabel,
    taskState,
    tripDayRange,
    vnTime,
} from './station-work-model'
import type { ReactNode } from 'react'
import type { WorkActivity } from '@/lib/api/work-activity-api'
import type { ScheduleState, TaskState } from './station-work-model'
import { m } from '@/lib/paraglide/messages'

interface StationWorkBoardProps {
    activities: WorkActivity[]
    /** Đồng hồ tiêm được để xem trước và test cho ra kết quả xác định. */
    now: Date
    /** Id vừa có biến động — lật vào như hàng đơn mới ở bảng đơn. */
    arrivedIds?: ReadonlySet<string>
}

/**
 * Màn 2 của máy trạm: dòng thời gian công việc trong ngày.
 *
 * Bảng đơn là hàng đợi xếp theo độ gấp; màn này là dòng thời gian, nên đọc theo
 * một cách khác: vạch "BÂY GIỜ" cắt ngang giữa việc đã qua và việc còn ở phía
 * trước. Giữ nguyên bảng màu, kiểu chữ và hiệu ứng lật của bảng đơn — đây là
 * cùng một cái bảng nói về việc khác, không phải phong cách thứ hai.
 */
export function StationWorkBoard({
    activities,
    now,
    arrivedIds,
}: StationWorkBoardProps) {
    const { schedule, trips, tasks } = groupStationWork(activities, now)
    const isEmpty =
        schedule.length === 0 && trips.length === 0 && tasks.length === 0

    // Chỉ mục kế tiếp mới được đếm ngược.
    const nextIdx = nowLineIndex(schedule, now)
    const nowIdx = nextIdx

    const upcomingCount = schedule.filter(
        (a) => scheduleState(a, now) !== 'past',
    ).length
    const overdueCount = tasks.filter(
        (a) => taskState(a, now) === 'overdue',
    ).length

    return (
        <div className="work-board flex min-h-0 flex-1 flex-col overflow-auto">
            <StationWorkStyles />

            {isEmpty ? (
                <div className="flex h-full items-center justify-center px-6">
                    <p className="empty-blink text-center text-xl tracking-[0.3em] text-[var(--dim)]">
                        —&nbsp;&nbsp;{m.station_work_empty()}&nbsp;&nbsp;—
                    </p>
                </div>
            ) : (
                <div className="min-w-[980px] flex-1">
                    <div className="station-chrome board-grid legend work-legend">
                        <span>{m.station_work_col_time()}</span>
                        <span>{m.station_work_col_content()}</span>
                        <span>{m.station_work_col_people()}</span>
                        <span>{m.station_work_col_state()}</span>
                    </div>

                    <div className="board-list">
                        {schedule.map((a, i) => (
                            <div key={a.id}>
                                {i === nowIdx && <NowLine now={now} />}
                                <ScheduleRow
                                    activity={a}
                                    now={now}
                                    isNext={i === nextIdx}
                                    arrived={arrivedIds?.has(a.id) ?? false}
                                />
                            </div>
                        ))}
                        {/* Mọi mục đã qua thì vạch nằm cuối danh sách. */}
                        {nowIdx >= schedule.length && <NowLine now={now} />}

                        {trips.length > 0 && (
                            <>
                                <SectionHead>
                                    {m.station_work_section_trips()}
                                </SectionHead>
                                {trips.map((a) => (
                                    <TripRow
                                        key={a.id}
                                        activity={a}
                                        arrived={arrivedIds?.has(a.id) ?? false}
                                    />
                                ))}
                            </>
                        )}

                        {tasks.length > 0 && (
                            <>
                                <SectionHead>
                                    {m.station_work_section_tasks()}
                                </SectionHead>
                                {tasks.map((a) => (
                                    <TaskRow
                                        key={a.id}
                                        activity={a}
                                        now={now}
                                        arrived={arrivedIds?.has(a.id) ?? false}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}

            <footer className="station-chrome work-foot">
                <div className="flex items-center gap-5">
                    <FootStat
                        tone="info"
                        count={upcomingCount}
                        label={m.station_work_summary_upcoming()}
                    />
                    <FootStat
                        tone="dim"
                        count={trips.length}
                        label={m.station_work_summary_trips()}
                    />
                    <FootStat
                        tone="alert"
                        count={overdueCount}
                        label={m.station_work_summary_overdue()}
                    />
                </div>
                <span className="font-semibold uppercase tracking-[0.18em] text-[var(--dim)]">
                    {m.station_work_summary_total({
                        date: now.toLocaleDateString('vi-VN', {
                            timeZone: 'Asia/Ho_Chi_Minh',
                            day: '2-digit',
                            month: '2-digit',
                        }),
                        count: String(
                            schedule.length + trips.length + tasks.length,
                        ),
                    })}
                </span>
            </footer>
        </div>
    )
}

/**
 * Vạch "BÂY GIỜ" — điểm nhấn của màn này. Nó tự trôi xuống giữa các hàng theo
 * đồng hồ, nên đứng cách vài mét vẫn thấy ngay cái gì đã qua và cái gì còn tới.
 */
function NowLine({ now }: { now: Date }) {
    return (
        <div className="work-now" role="separator">
            <span className="work-now-rule" />
            <span className="work-now-label">
                {vnTime(now.toISOString())} · {m.station_work_now()}
            </span>
            <span className="work-now-rule" />
        </div>
    )
}

function SectionHead({ children }: { children: ReactNode }) {
    return <div className="work-section">{children}</div>
}

function FootStat({
    tone,
    count,
    label,
}: {
    tone: 'info' | 'dim' | 'alert'
    count: number
    label: string
}) {
    return (
        <span className="flex items-center gap-2">
            <span className={`led led-${tone}`} />
            <span className="text-sm font-bold tabular-nums text-[var(--ink)]">
                {count}
            </span>
            <span className="uppercase tracking-[0.12em] text-[var(--dim)]">
                {label}
            </span>
        </span>
    )
}

const SCHEDULE_LABEL: Record<ScheduleState, () => string> = {
    past: m.station_work_state_done,
    running: m.station_work_state_running,
    soon: m.station_work_state_upcoming,
    upcoming: m.station_work_state_upcoming,
}

function ScheduleRow({
    activity,
    now,
    isNext,
    arrived,
}: {
    activity: WorkActivity
    now: Date
    isNext: boolean
    arrived: boolean
}) {
    const state = scheduleState(activity, now)
    // Đếm ngược thay nhãn trạng thái, nhưng CHỈ ở mục kế tiếp: hàng nào cũng
    // đếm thì cả bảng thành một dãy số nhấp nháy.
    const countdown =
        isNext && state !== 'running' && state !== 'past'
            ? countdownLabel(activity.starts_at, now)
            : ''

    return (
        <WorkRow
            state={state}
            arrived={arrived}
            time={scheduleTimeRange(activity, now)}
            title={activity.title}
            people={peopleLabel(activity)}
            tag={countdown || SCHEDULE_LABEL[state]()}
            tagTone={countdown ? 'soon' : state}
        />
    )
}

function TripRow({
    activity,
    arrived,
}: {
    activity: WorkActivity
    arrived: boolean
}) {
    return (
        <WorkRow
            state="running"
            arrived={arrived}
            time={tripDayRange(activity)}
            title={activity.title}
            people={peopleLabel(activity)}
            tag={m.station_work_state_running()}
            tagTone="running"
        />
    )
}

const TASK_LABEL: Record<TaskState, () => string> = {
    done: m.station_work_state_done,
    overdue: m.station_work_state_overdue,
    soon: m.station_work_state_upcoming,
    upcoming: m.station_work_state_upcoming,
}

function TaskRow({
    activity,
    now,
    arrived,
}: {
    activity: WorkActivity
    now: Date
    arrived: boolean
}) {
    const state = taskState(activity, now)
    // Chỉ đếm ngược khi đã sát hạn. Việc còn ba tiếng nữa mà cũng đếm thì cột
    // trạng thái thành một dãy số, trong khi cột giờ đã ghi hạn rồi.
    const countdown =
        state === 'soon' ? countdownLabel(activity.ends_at, now) : ''

    return (
        <WorkRow
            state={state === 'done' ? 'past' : state}
            arrived={arrived}
            time={taskDueLabel(activity, now)}
            title={activity.title}
            people={peopleLabel(activity)}
            tag={countdown || TASK_LABEL[state]()}
            tagTone={state === 'done' ? 'past' : state}
        />
    )
}

type RowTone = ScheduleState | TaskState

function WorkRow({
    state,
    arrived,
    time,
    title,
    people,
    tag,
    tagTone,
}: {
    state: RowTone
    arrived: boolean
    time: string
    title: string
    people: string
    tag: string
    tagTone: RowTone
}) {
    return (
        <div
            className={`board-grid work-row ${state} ${arrived ? 'arrive' : ''}`}
        >
            <span className="work-time">{time}</span>
            <span className="work-title">{title}</span>
            <span className="work-people">{people}</span>
            <span className="flex">
                <span className={`gate-tag work-tag-${tagTone}`}>{tag}</span>
            </span>
        </div>
    )
}

function StationWorkStyles() {
    return (
        <style>{`
/* The work board reuses the order board's color tokens and flap animation
   (station-shell); only the column grid and the NOW rule are new.
   Comments here stay in English: this block is a template literal, so the
   no-hardcoded-vietnamese rule reads it as a user-facing string. */
.work-board{ background:var(--panel); }

.board-grid.work-row, .board-grid.legend.work-legend{
    grid-template-columns:172px minmax(260px,1fr) 250px 172px;
}

.work-row{
    position:relative; min-height:66px;
    border-bottom:1px solid var(--line);
    align-items:center;
    transform-style:preserve-3d; transform-origin:top center;
}
.work-row::before{
    content:""; position:absolute; left:0; top:0; bottom:0; width:4px;
    background:var(--gate);
    box-shadow:0 0 8px color-mix(in srgb, var(--gate) 55%, transparent);
    opacity:.65;
}
.work-row.past{ --gate:var(--dim); opacity:.55; }
.work-row.running{ --gate:var(--live); }
.work-row.soon{ --gate:var(--amber); }
.work-row.upcoming{ --gate:var(--info); }
.work-row.overdue{ --gate:var(--alert); }
.work-row.done{ --gate:var(--dim); opacity:.55; }

.work-row.arrive{
    background:color-mix(in srgb, var(--amber) 12%, var(--panel));
    animation: stationFlap 720ms cubic-bezier(.2,.9,.2,1) both;
}
.work-row.arrive::before{ opacity:1; }
.work-row.arrive::after{
    content:""; position:absolute; inset:0; pointer-events:none;
    background:linear-gradient(100deg, transparent 22%,
        color-mix(in srgb, var(--amber) 24%, transparent) 50%, transparent 78%);
    transform:translateX(-100%);
    animation: stationSweep 900ms ease 140ms 1;
}

/* Type scale matches the order board (station.tsx StationRow): the two screens
   alternate every 30s, so a different scale forces the eye to readjust on every
   swap. Time and secondary columns use 1rem (text-base); the headline row uses
   1.125rem (text-lg) with font-black. No backticks in this block. */
.work-time{
    font-size:1rem; font-weight:700; letter-spacing:.01em;
    font-variant-numeric:tabular-nums; color:var(--dim);
}
.work-title{
    font-size:1.125rem; font-weight:900; letter-spacing:.015em;
    line-height:1.25; color:var(--ink);
    overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.work-people{
    font-size:1rem; font-weight:700; color:var(--dim);
    overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}

/* --- NOW rule --- */
.work-now{
    display:flex; align-items:center; gap:.9rem;
    padding:.55rem 1.25rem;
    background:color-mix(in srgb, var(--alert) 6%, var(--panel));
    border-bottom:1px solid var(--line);
}
.work-now-rule{
    flex:1 1 auto; height:2px; border-radius:2px;
    background:linear-gradient(90deg,
        transparent, color-mix(in srgb, var(--alert) 70%, transparent));
}
.work-now-rule:last-child{
    background:linear-gradient(90deg,
        color-mix(in srgb, var(--alert) 70%, transparent), transparent);
}
.work-now-label{
    flex:none; font-size:.86rem; font-weight:900;
    letter-spacing:.2em; text-transform:uppercase; color:var(--alert);
    font-variant-numeric:tabular-nums;
}

/* --- Section head --- */
.work-section{
    padding:.85rem 1.25rem .5rem;
    font-size:.72rem; font-weight:900; letter-spacing:.18em;
    text-transform:uppercase; color:var(--dim);
    background:var(--panel-2); border-bottom:1px solid var(--line);
}

/* --- Status tags --- */
.work-tag-past, .work-tag-done{ color:var(--dim);
    background:var(--panel-2); border-color:var(--line); }
.work-tag-running{ color:var(--live);
    background:color-mix(in srgb, var(--live) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--live) 40%, transparent); }
.work-tag-soon{ color:var(--amber);
    background:color-mix(in srgb, var(--amber) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--amber) 42%, transparent);
    animation: stationPulse 1.8s ease-in-out infinite; }
.work-tag-upcoming{ color:var(--info);
    background:color-mix(in srgb, var(--info) 12%, var(--panel));
    border-color:color-mix(in srgb, var(--info) 38%, transparent); }
.work-tag-overdue{ color:var(--alert);
    background:color-mix(in srgb, var(--alert) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--alert) 42%, transparent); }

.led-info{ color:var(--info); }
.led-dim{ color:var(--dim); }
.led-alert{ color:var(--alert); }

/* --- Footer rail --- */
.work-foot{
    display:flex; align-items:center; justify-content:space-between; gap:1rem;
    border-top:1px solid var(--line);
    padding:.625rem 1.25rem; font-size:.75rem;
}

@media (prefers-reduced-motion: reduce){
    .work-row.arrive{ animation:none; }
    .work-row.arrive::after{ display:none; }
    .work-tag-soon{ animation:none; }
}
`}</style>
    )
}
